import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { checkboxHeaderCellClass } from '../styles/tableStyles';

const GenericDataGrid = (props) => {

  const {
    rows,
    columns,
    rowCount,
    page,
    pageSize,
    onPaginationChange,
    checkbox,
    onSelectionChange,
    loading = false,
    errors,
    dataGridSx,
    tableMinWidth,
    autoHeight = true,
    // Scopes queries to this grid. Defaulted so every list has a handle without
    // touching ten call sites; pass one explicitly on a screen that shows more
    // than one grid at a time.
    //
    // Rows are deliberately NOT given ids here. MUI already renders each row as
    // .MuiDataGrid-row with a data-id carrying the row's key, which is enough to
    // find and identify one, and injecting attributes into the grid's internals
    // would break on a MUI upgrade for no gain.
    testId = 'data-grid',
    ...rest
  } = props

  return (
    <Box
      data-testid={testId}
      className={tableMinWidth ? 'table-scroll-host' : undefined}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 0,
        backgroundColor: '#FFFFFF',
        overflowX: tableMinWidth ? 'auto' : 'visible',
        overflowY: 'hidden',
        pb: tableMinWidth ? 1 : 0,
        scrollbarWidth: tableMinWidth ? 'thin' : 'auto',
        scrollbarColor: tableMinWidth ? '#C7D2F0 transparent' : 'auto',
        WebkitOverflowScrolling: 'touch',
        ...(tableMinWidth
          ? {
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 12,
                border: '1px solid #DFE5F6',
                borderRadius: '12px',
                pointerEvents: 'none',
                zIndex: 0,
              },
            }
          : {}),
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          minWidth: tableMinWidth ? `${tableMinWidth}px` : 0,
          maxWidth: tableMinWidth ? 'none' : '100%',
          boxSizing: 'border-box',
        }}
      >
        <div>
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight={autoHeight}
            pagination
            paginationMode="server"
            rowCount={Number(rowCount)}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={({ page, pageSize }) => onPaginationChange(page, pageSize)}
            checkboxSelection={checkbox === 'true' ? true : false}
            // With server pagination only the current page is in `rows`; without this the grid
            // calls getRowParams() on selected ids that are not in `rows` (e.g. a just-deleted row
            // or selections on another page) and throws "No row with id ... found".
            keepNonExistentRowsSelected
            onRowSelectionModelChange={(newSelection) => {
              console.log('DataGrid selection changed:', newSelection);
              !loading && onSelectionChange?.(newSelection);
            }}
            disableRowSelectionOnClick
            disableColumnMenu
            pageSizeOptions={[10,20,50]}
            localeText={{
              noRowsLabel: errors || 'No data present in this table',
              noResultsOverlayLabel: errors,
            }}
            hideFooterSelectedRowCount
            slotProps={{
              columnHeader: ({ colDef }) => ({
                className: colDef.field === 'select' ? checkboxHeaderCellClass : '',
              }),
            }}
            sx={{
              width: '100%',
              ...(tableMinWidth
                ? {
                    border: 'none',
                    borderRadius: 0,
                    backgroundColor: 'transparent',
                    '& .MuiDataGrid-main': {
                      overflow: 'hidden',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      overflowX: 'hidden !important',
                    },
                    '& .MuiDataGrid-scrollbar--horizontal': {
                      display: 'none',
                    },
                  }
                : {}),
              ...dataGridSx,
            }}
            {...rest}
          />
        </div>
      </Box>
    </Box>
  );
};

export default GenericDataGrid;
